import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import Navbar from '../components/nav/Navbar';
import { useDiscover } from '../hooks/useDiscover';
import { useAuth } from '../context/AuthContext';
import supabase from '../supabase/supabase';

const {
  FiArrowRight, FiClock, FiFilter, FiArrowLeft, FiHeart,
  FiMusic, FiHeadphones, FiTrendingUp, FiZap, FiLoader, FiStar
} = FiIcons;

const GENRE_FILTERS = ['All', 'Hip-Hop', 'R&B', 'Electronic', 'Pop', 'Alt-Pop', 'Neo-Soul'];
const MOOD_FILTERS  = ['All Moods', 'Dark', 'Intense', 'Euphoric', 'Melancholic'];

const DropCard = ({ drop, featured = false, isWished, onToggleWishlist }) => (
  <div className={`relative group block ${featured ? 'md:col-span-2 md:row-span-2' : ''}`}>
    <Link to={`/drop/${drop.id}`} className="block h-full">
      <div className={`relative overflow-hidden rounded-3xl glass-card h-full transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(0,0,0,0.5)]`}>
        <div className={`relative ${featured ? 'aspect-video md:aspect-[2/1]' : 'aspect-square'}`}>
          {drop.cover_url ? (
            <img src={drop.cover_url} alt={drop.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-brand-pink/20 to-brand-coral/20 flex items-center justify-center">
              <SafeIcon icon={FiMusic} className="text-4xl text-white/20" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />
          
          {drop.tags?.length > 0 && (
            <div className="absolute top-4 left-4 flex gap-2 flex-wrap z-10">
              {drop.tags.slice(0, 2).map(tag => (
                <span key={tag} className="px-2 py-1 bg-zinc-950/80 backdrop-blur text-[10px] font-black tracking-widest text-brand-pink border border-brand-pink/30 rounded-lg pointer-events-none">{tag}</span>
              ))}
            </div>
          )}
          
          {drop.genre && (
            <div className="absolute top-4 right-16 z-10">
              <span className="px-2 py-1 bg-zinc-950/80 backdrop-blur text-[10px] font-black tracking-widest text-zinc-400 border border-white/10 rounded-lg pointer-events-none">{drop.genre}</span>
            </div>
          )}
        </div>
        <div className="absolute bottom-0 left-0 w-full p-6 pointer-events-none">
          <p className="text-zinc-400 text-xs font-black tracking-[0.2em] mb-1.5 uppercase">
            {drop.artist?.name || drop.artist_name || 'Unknown Artist'}
          </p>
          <h3 className={`font-black uppercase leading-tight text-white mb-4 ${featured ? 'text-3xl md:text-4xl' : 'text-xl'}`}>
            {drop.title}
          </h3>
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-500 flex items-center gap-1.5 font-bold">
              <SafeIcon icon={FiClock} className="text-xs" />
              {drop.min_price > 0 ? `From $${drop.min_price}` : 'Free'}
            </span>
            <span className="text-brand-pink font-black text-sm flex items-center gap-1.5 group-hover:translate-x-1 transition-transform">
              Unlock <SafeIcon icon={FiArrowRight} className="text-xs" />
            </span>
          </div>
        </div>
      </div>
    </Link>

    {/* Wishlist Button - Overlayed on top of the link so it's clickable */}
    <button 
      onClick={(e) => onToggleWishlist(drop.id, e)}
      className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full glass-card flex items-center justify-center hover:scale-110 transition-all shadow-xl hover:border-brand-pink/40"
    >
      <SafeIcon 
        icon={FiHeart} 
        className={`text-lg transition-colors ${isWished ? 'text-brand-pink' : 'text-white'}`} 
        style={isWished ? { fill: 'currentColor' } : {}} 
      />
    </button>
  </div>
);

const DiscoverRow = ({ title, icon, drops, wishlist, onToggleWishlist }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2">
      <SafeIcon icon={icon} className="text-brand-pink" />
      <h3 className="text-xs font-black uppercase tracking-widest text-zinc-400">{title}</h3>
    </div>
    <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
      {drops.map(drop => {
        const isWished = wishlist.has(drop.id);
        return (
          <div key={drop.id} className="flex-shrink-0 w-48 relative group">
            <Link to={`/drop/${drop.id}`} className="block">
              <div className="w-48 h-48 rounded-2xl overflow-hidden glass-card mb-3 relative">
                {drop.cover_url
                  ? <img src={drop.cover_url} alt={drop.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  : <div className="w-full h-full flex items-center justify-center"><SafeIcon icon={FiMusic} className="text-zinc-700 text-3xl" /></div>
                }
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                  <span className="text-brand-pink text-[10px] font-black uppercase tracking-widest">View Drop</span>
                </div>
              </div>
              <p className="text-xs font-black uppercase tracking-tight text-white truncate">{drop.title}</p>
              <p className="text-[10px] font-bold text-zinc-500 truncate">{drop.artist?.name || drop.artist_name}</p>
            </Link>
            <button 
              onClick={(e) => onToggleWishlist(drop.id, e)}
              className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/40 backdrop-blur-md border border-white/10 flex items-center justify-center hover:scale-110 transition-transform"
            >
              <SafeIcon 
                icon={FiHeart} 
                className={`text-sm ${isWished ? 'text-brand-pink' : 'text-white'}`} 
                style={isWished ? { fill: 'currentColor' } : {}} 
              />
            </button>
          </div>
        );
      })}
    </div>
  </div>
);

const Discover = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const urlQuery = searchParams.get('q') || '';

  const [genreFilter, setGenreFilter] = useState('All');
  const [moodFilter,  setMoodFilter]  = useState('All Moods');
  
  const [wishlist, setWishlist] = useState(new Set());
  const [forYouDrops, setForYouDrops] = useState([]);

  const { drops, loading, error } = useDiscover({
    genre: genreFilter,
    mood:  moodFilter,
    query: urlQuery,
  });

  // Fetch Personalized & Wishlist data
  useEffect(() => {
    if (!user) return;
    
    // Fetch Wishlist
    supabase.from('d2f_wishlist_items')
      .select('drop_id')
      .eq('user_id', user.id)
      .then(({data}) => setWishlist(new Set((data || []).map(d => d.drop_id))));
      
    // Fetch For You (only if no active search)
    if (!urlQuery) {
      supabase.rpc('get_personalized_feed', { p_user_id: user.id, p_limit: 8 })
        .then(({data}) => setForYouDrops(data || []));
    }
  }, [user, urlQuery]);

  const handleToggleWishlist = async (dropId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return navigate('/auth');
    
    const newSet = new Set(wishlist);
    if (newSet.has(dropId)) newSet.delete(dropId);
    else newSet.add(dropId);
    setWishlist(newSet);
    
    await supabase.rpc('toggle_wishlist', { p_user_id: user.id, p_drop_id: dropId });
  };

  const featured = drops[0];
  const rest     = drops.slice(1);

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <Navbar />
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="ambient-orb-pink  w-[700px] h-[700px] top-[-20%] right-[-15%] animate-caustic" />
        <div className="ambient-orb-coral w-[500px] h-[500px] bottom-[-10%] left-[-10%] animate-caustic" style={{ animationDelay: '3s' }} />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 space-y-12">
        <div className="space-y-6">
          <button onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors group">
            <div className="w-8 h-8 rounded-xl glass-card flex items-center justify-center group-hover:border-brand-pink/30 transition-all">
              <SafeIcon icon={FiArrowLeft} className="text-sm" />
            </div>
            <span className="text-xs font-black uppercase tracking-widest">Back</span>
          </button>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
            <div>
              <span className="text-xs font-black tracking-widest text-brand-pink uppercase">Curated Drops</span>
              <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tighter text-white mt-1">
                {urlQuery ? `"${urlQuery}"` : 'Live Drops'}
              </h1>
              <p className="text-zinc-500 mt-2 font-bold text-sm">
                {loading ? 'Loading…' : `${drops.length} drop${drops.length !== 1 ? 's' : ''} available`}
              </p>
            </div>
          </div>

          {!urlQuery && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <SafeIcon icon={FiFilter} className="text-zinc-600 text-sm" />
                <span className="text-[10px] font-black uppercase tracking-widest text-zinc-600">Genre:</span>
                {GENRE_FILTERS.map(f => (
                  <button key={f} onClick={() => setGenreFilter(f)}
                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                      genreFilter === f
                        ? 'bg-gradient-to-r from-brand-pink to-brand-coral text-white shadow-[0_0_12px_rgba(255,102,204,0.3)]'
                        : 'glass-card text-zinc-500 hover:text-white hover:border-brand-pink/20'
                    }`}>{f}</button>
                ))}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <SafeIcon icon={FiZap} className="text-zinc-600 text-sm" />
                <span className="text-[10px] font-black uppercase tracking-widest text-zinc-600">Mood:</span>
                {MOOD_FILTERS.map(f => (
                  <button key={f} onClick={() => setMoodFilter(f)}
                    className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                      moodFilter === f
                        ? 'bg-white/10 text-white border border-white/20'
                        : 'glass-subtle text-zinc-600 hover:text-white'
                    }`}>{f}</button>
                ))}
              </div>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <SafeIcon icon={FiLoader} className="text-3xl text-brand-pink animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-24 space-y-4">
            <p className="text-red-400 font-black uppercase tracking-widest text-sm">{error}</p>
            <p className="text-zinc-600 text-xs font-bold">Could not load drops. Please try again.</p>
          </div>
        ) : drops.length === 0 ? (
          <div className="flex flex-col items-center gap-6 py-24 text-center">
            <div className="w-20 h-20 rounded-3xl glass-card flex items-center justify-center">
              <SafeIcon icon={FiHeadphones} className="text-3xl text-zinc-700" />
            </div>
            <div>
              <p className="text-white font-black text-xl uppercase tracking-tight">No drops found</p>
              <p className="text-zinc-600 text-sm font-bold mt-1">
                {urlQuery ? `No results for "${urlQuery}"` : 'Try a different filter'}
              </p>
            </div>
            <button onClick={() => { setGenreFilter('All'); setMoodFilter('All Moods'); }}
              className="px-6 py-3 btn-brand rounded-2xl text-xs">Clear Filters</button>
          </div>
        ) : (
          <>
            {!urlQuery && user && forYouDrops.length > 0 && genreFilter === 'All' && moodFilter === 'All Moods' && (
              <DiscoverRow 
                title="Recommended For You" 
                icon={FiStar} 
                drops={forYouDrops} 
                wishlist={wishlist}
                onToggleWishlist={handleToggleWishlist}
              />
            )}
            
            {!urlQuery && (
              <DiscoverRow 
                title="Trending Now" 
                icon={FiTrendingUp} 
                drops={drops.slice(0, 8)} 
                wishlist={wishlist}
                onToggleWishlist={handleToggleWishlist}
              />
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured && (
                <DropCard 
                  drop={featured} 
                  featured 
                  isWished={wishlist.has(featured.id)} 
                  onToggleWishlist={handleToggleWishlist} 
                />
              )}
              {rest.map(drop => (
                <DropCard 
                  key={drop.id} 
                  drop={drop} 
                  isWished={wishlist.has(drop.id)} 
                  onToggleWishlist={handleToggleWishlist} 
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Discover;